<template>
  <teleport to="body">
    <div
      v-if="isVisible"
      class="tutorial-overlay"
    >
      <div class="tutorial-overlay__backdrop" />

      <div
        v-if="targetRect"
        class="tutorial-spotlight"
        :style="spotlightStyle"
        @click="handleSpotlightClick"
      />

      <TutorialTooltip
        v-if="currentStepData"
        :title="currentStepData.title"
        :description="currentStepData.description"
        :position="currentStepData.position"
        :current-step="displayStep"
        :total-steps="displayTotalSteps"
        :requires-click="currentStepData.requiresClick"
        :target-rect="targetRect"
        :has-target="!!targetRect"
        @next="handleNext"
        @skip="handleSkip"
      />
    </div>
  </teleport>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router";
import useTutorial from "@composables/useTutorial";
import { getVisibleSteps } from "./tutorialSteps";
import TutorialTooltip from "./TutorialTooltip.vue";

export default {
  name: "OnboardingTutorial",
  components: {
    TutorialTooltip,
  },
  props: {
    hasTickets: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["complete"],
  setup(props, { emit }) {
    const instance = getCurrentInstance();
    const route = useRoute();
    const router = useRouter();
    const {
      isActive,
      currentIndex,
      currentStepData,
      totalSteps,
      displayStep: stepPosition,
      isLastStep,
      startTutorial,
      nextStep,
      goToIndex,
      steps,
      completeTutorial,
    } = useTutorial();

    const targetRect = ref(null);

    const currentRouteName = computed(() => String(route.name || ""));

    function stepMatchesRoute(step, routeName) {
      if (!step) return false;

      const routes = Array.isArray(step.route) ? step.route : [step.route];
      return routes.includes(routeName);
    }

    // Telas cobertas por algum passo. Fora delas o tutorial se esconde, mesmo
    // que o alvo exista (os itens do menu lateral estao em todas as telas).
    const isRouteInTutorial = computed(() =>
      steps.value.some(step => stepMatchesRoute(step, currentRouteName.value))
    );

    // Dentro do territorio, o alvo de um passo pode estar em outra rota: as abas
    // do paciente sao renderizadas pela tela pai, entao seguem visiveis.
    const isVisible = computed(
      () =>
        isActive.value &&
        isRouteInTutorial.value &&
        (stepMatchesRoute(currentStepData.value, currentRouteName.value) ||
          targetRect.value !== null)
    );

    const resizeObserver = ref(null);
    let removeRouterGuard = null;

    const displayStep = computed(() => stepPosition.value);

    const displayTotalSteps = computed(() => totalSteps.value);

    const spotlightStyle = computed(() => {
      if (!targetRect.value) return {};

      const padding = currentStepData.value?.highlightPadding || 8;

      return {
        top: `${targetRect.value.top - padding}px`,
        left: `${targetRect.value.left - padding}px`,
        width: `${targetRect.value.width + padding * 2}px`,
        height: `${targetRect.value.height + padding * 2}px`,
      };
    });

    function findTargetElement() {
      if (!currentStepData.value) return null;

      const selector = currentStepData.value.targetSelector;
      return document.querySelector(selector);
    }

    function updateTargetRect() {
      const element = findTargetElement();

      if (element) {
        const rect = element.getBoundingClientRect();
        // Only update if element has valid dimensions
        if (rect.width > 0 && rect.height > 0) {
          targetRect.value = {
            top: rect.top,
            left: rect.left,
            right: rect.right,
            bottom: rect.bottom,
            width: rect.width,
            height: rect.height,
          };
        } else {
          // Element exists but not rendered yet, retry
          setTimeout(updateTargetRect, 100);
        }
      } else {
        targetRect.value = null;
      }
    }

    let emptyCheckRetries = 0;
    const MAX_EMPTY_CHECK_RETRIES = 5;

    function checkForEmptyTicketsList() {
      if (currentIndex.value === 0) {
        const conversationList = document.querySelector("[data-tutorial='conversation-list']");
        if (conversationList) {
          // Se o conteúdo ainda está carregando, aguardar e tentar novamente
          const container = document.querySelector(".patient-ticket-list");
          if (container && container.classList.contains("loading") && emptyCheckRetries < MAX_EMPTY_CHECK_RETRIES) {
            emptyCheckRetries++;
            setTimeout(() => checkForEmptyTicketsList(), 500);
            return false;
          }

          const hasVisibleContent = Array.from(conversationList.children).some(
            (child) => child.style.display !== "none"
          );
          if (!hasVisibleContent) {
            // Sem chamados os passos de atendimento nao tem como ser
            // percorridos, mas os do menu lateral sim: refiltra em vez de
            // reduzir o tutorial a um passo.
            restartWithoutTickets();
            return true;
          }
        }
      }
      return false;
    }

    function handleSpotlightClick() {
      if (currentStepData.value?.requiresClick) {
        const element = findTargetElement();
        if (element) {
          element.click();
        }
      }
    }

    async function callCompleteTutorialApi() {
      const app = instance?.appContext?.config?.globalProperties;
      if (!app) return;

      try {
        const session = app.$Session.get(app.$userSessionName);
        if (!session?.token?.Value) return;

        await app.$axios.put(
          "api/professional/completeTutorial",
          {},
          {
            headers: {
              Authorization: `bearer ${session.token.Value}`,
            },
          }
        );

        session.showTutorial = false;
        app.$Session.set(app.$userSessionName, session);
      } catch (error) {
        console.error("Error completing tutorial:", error);
      }
    }

    function buildSteps(hasTickets) {
      const app = instance?.appContext?.config?.globalProperties;

      return getVisibleSteps({
        hasManagementAccess: app ? app.$Utilities.hasManagementAccess(app) : false,
        hasFeature: (feature) => (app ? app.$Utilities.hasAccessToFeature(app, feature) : false),
        hasTickets,
      });
    }

    function restartWithoutTickets() {
      const withoutTicketSteps = buildSteps(false);

      completeTutorial();
      startTutorial(withoutTicketSteps);

      // isActive volta a true no mesmo tick, entao o watch nao dispara e o
      // destaque ficaria no alvo do passo anterior.
      targetRect.value = null;
      setTimeout(updateTargetRect, 300);
    }

    async function handleNext() {
      if (isLastStep.value) {
        await finishTutorial();
      } else {
        nextStep();
      }
    }

    async function handleSkip() {
      await finishTutorial();
    }

    async function finishTutorial() {
      await callCompleteTutorialApi();
      completeTutorial();
      emit("complete");
    }

    function handleRouteChange(routeName) {
      if (!isActive.value) return;

      if (stepMatchesRoute(currentStepData.value, routeName)) {
        setTimeout(updateTargetRect, 300);
        return;
      }

      if (stepMatchesRoute(steps.value[currentIndex.value + 1], routeName)) {
        goToIndex(currentIndex.value + 1);
        setTimeout(updateTargetRect, 300);
        return;
      }

      // O usuario voltou para uma tela ja percorrida: retoma o passo dela em vez
      // de deixar o balao orfao sobre uma tela sem alvo.
      for (let index = currentIndex.value - 1; index >= 0; index--) {
        if (stepMatchesRoute(steps.value[index], routeName)) {
          goToIndex(index);
          setTimeout(updateTargetRect, 300);
          return;
        }
      }
    }

    function setupResizeObserver() {
      resizeObserver.value = new ResizeObserver(() => {
        if (isActive.value) {
          updateTargetRect();
        }
      });

      resizeObserver.value.observe(document.body);
    }

    function cleanupResizeObserver() {
      if (resizeObserver.value) {
        resizeObserver.value.disconnect();
        resizeObserver.value = null;
      }
    }

    watch(isActive, (active) => {
      if (active) {
        // Wait for page to fully render before calculating positions
        setTimeout(() => {
          if (!checkForEmptyTicketsList()) {
            updateTargetRect();
            // Retry after a short delay to ensure element is properly positioned
            setTimeout(updateTargetRect, 300);
          }
        }, 600);
      }
    });

    watch(currentIndex, () => {
      setTimeout(updateTargetRect, 300);
    });

    // Ao reaparecer (usuario voltou para a tela do passo), o alvo precisa ser
    // medido de novo: o anterior ficou obsoleto quando a rota mudou.
    watch(isVisible, (visible) => {
      if (visible) setTimeout(updateTargetRect, 300);
    });

    watch(
      () => props.hasTickets,
      (value) => {
        if (!value) restartWithoutTickets();
      }
    );

    onMounted(() => {
      setupResizeObserver();
      window.addEventListener("resize", updateTargetRect);
      window.addEventListener("scroll", updateTargetRect, true);

      // Handle current route on mount
      if (route.name) {
        handleRouteChange(String(route.name));
      }

      // Listen for route changes
      removeRouterGuard = router.afterEach((to) => {
        if (to.name) {
          handleRouteChange(String(to.name));
        }
      });
    });

    onBeforeUnmount(() => {
      cleanupResizeObserver();
      window.removeEventListener("resize", updateTargetRect);
      window.removeEventListener("scroll", updateTargetRect, true);
      if (removeRouterGuard) {
        removeRouterGuard();
      }
    });

    return {
      buildSteps,
      isActive,
      isVisible,
      currentIndex,
      totalSteps,
      currentStepData,
      targetRect,
      spotlightStyle,
      displayStep,
      displayTotalSteps,
      startTutorial,
      handleNext,
      handleSkip,
      handleSpotlightClick,
      handleRouteChange,
      updateTargetRect,
      checkForEmptyTicketsList,
    };
  },
  methods: {
    start(hasTickets = true) {
      this.startTutorial(this.buildSteps(hasTickets));
    },
  },
};
</script>

<style scoped>
.tutorial-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  pointer-events: none;
}

.tutorial-overlay__backdrop {
  display: none;
}

.tutorial-spotlight {
  position: absolute;
  border-radius: 8px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6);
  pointer-events: none;
  transition: all 0.3s ease;
}

.tutorial-spotlight::before {
  content: "";
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border: 2px solid #4f3f82;
  border-radius: 12px;
  animation: pulse 2s infinite;
  pointer-events: none;
}

@keyframes pulse {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.02);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
